import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Tabs, 
  Tab, 
  Paper, 
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  People as PeopleIcon,
  Key as KeyIcon,
  AutoAwesome as AutoAwesomeIcon,
  Mic as MicIcon,
  Analytics as AnalyticsIcon,
  AdminPanelSettings as AdminIcon,
  Email as EmailIcon,
  CloudSync as CloudSyncIcon,
  BugReport as BugReportIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { isUserAdmin } from '../../firebase/services/userService';

// Composants de l'administration
import UserManagement from './components/UserManagement';
import TranscriptionSettings from './components/TranscriptionSettings';
import SummarySettings from './components/SummarySettings';
import ApiSettings from './components/ApiSettings';
import StatisticsPanel from './components/StatisticsPanel';
import AccessDenied from './components/AccessDenied';
import EmailTestPanel from './components/EmailTestPanel';
import DiagnosticsPanel from '../../components/admin/DiagnosticsPanel';
import SystemLogsPage from './SystemLogsPage';
import MeetingsExport from './components/MeetingsExport';

// Panneau de tabulation avec style glassmorphisme
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ 
          p: 0,
          background: 'transparent'
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

const AdminPage = () => {
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const [value, setValue] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Détection de Firefox pour optimiser les effets CSS
  const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const adminStatus = await isUserAdmin(currentUser.uid);
        setIsAdmin(adminStatus);
      } catch (err) {
        addSystemNotification('Erreur lors de la vérification du statut administrateur:', 'error');
        setError("Impossible de vérifier les droits d'administration.");
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Configuration du style moderne sobre
  const glassContainerSx = {
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden'
  };

  const tabsSx = {
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    '& .MuiTabs-indicator': {
      backgroundColor: '#3F51B5',
      height: 3,
      borderRadius: '3px 3px 0 0',
    },
    '& .MuiTabs-scroller': {
      backgroundColor: '#f8fafc',
      borderRadius: '16px 16px 0 0',
      borderBottom: 'none',
    }
  };

  const tabSx = {
    minHeight: 72,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#6b7280',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      color: '#3F51B5',
      backgroundColor: 'rgba(63, 81, 181, 0.08)',
    },
    '&.Mui-selected': {
      color: '#3F51B5',
      backgroundColor: 'white',
      fontWeight: 700,
    },
    '& .MuiTab-iconWrapper': {
      marginBottom: '4px',
    }
  };

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box sx={{ 
          textAlign: 'center',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          p: 4
        }}>
          <CircularProgress sx={{ mb: 2, color: '#3F51B5' }} size={40} />
          <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
            Vérification des permissions...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}>
        <Alert 
          severity="error" 
          sx={{ 
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            maxWidth: 500
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      p: { xs: 1, sm: 2, md: 3 }
    }}>
      <Container maxWidth="xl">
        
        {/* Container principal avec tabs */}
        <Box sx={glassContainerSx}>
          <Tabs 
            value={value} 
            onChange={handleChange} 
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons="auto"
            sx={tabsSx}
          >
            <Tab 
              icon={<PeopleIcon />} 
              label="Utilisateurs" 
              sx={tabSx}
              {...a11yProps(0)} 
            />
            <Tab 
              icon={<KeyIcon />} 
              label="Clés API" 
              sx={tabSx}
              {...a11yProps(1)} 
            />
            <Tab 
              icon={<AutoAwesomeIcon />} 
              label="Résumé IA" 
              sx={tabSx}
              {...a11yProps(2)} 
            />
            <Tab 
              icon={<MicIcon />} 
              label="Transcription" 
              sx={tabSx}
              {...a11yProps(3)} 
            />
            <Tab 
              icon={<AnalyticsIcon />} 
              label="Statistiques" 
              sx={tabSx}
              {...a11yProps(4)} 
            />
            <Tab 
              icon={<EmailIcon />} 
              label="Tests Email" 
              sx={tabSx}
              {...a11yProps(5)} 
            />
            <Tab
              icon={<CloudSyncIcon />}
              label="Diagnostic OneDrive"
              sx={tabSx}
              {...a11yProps(6)}
            />
            <Tab
              icon={<FileDownloadIcon />}
              label="Export Réunions"
              sx={tabSx}
              {...a11yProps(7)}
            />
            <Tab
              icon={<BugReportIcon />}
              label="Logs Système"
              sx={tabSx}
              {...a11yProps(8)}
            />
          </Tabs>
          
          {/* Contenu des tabs avec background glassmorphisme */}
          <Box sx={{
            backgroundColor: '#fafbfc',
            minHeight: 'calc(100vh - 300px)',
            position: 'relative'
          }}>
            <TabPanel value={value} index={0}>
              <UserManagement />
            </TabPanel>
            
            <TabPanel value={value} index={1}>
              <ApiSettings />
            </TabPanel>
            
            <TabPanel value={value} index={2}>
              <SummarySettings />
            </TabPanel>
            
            <TabPanel value={value} index={3}>
              <TranscriptionSettings />
            </TabPanel>
            
            <TabPanel value={value} index={4}>
              <StatisticsPanel />
            </TabPanel>

            <TabPanel value={value} index={5}>
              <EmailTestPanel />
            </TabPanel>

            <TabPanel value={value} index={6}>
              <DiagnosticsPanel />
            </TabPanel>

            <TabPanel value={value} index={7}>
              <MeetingsExport />
            </TabPanel>

            <TabPanel value={value} index={8}>
              <SystemLogsPage />
            </TabPanel>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AdminPage; 